import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SelectExtended } from '.';
import { styled, CTA } from '@volkswagen-onehub/components-core';
import { useOneFormContext } from 'src/feature-app/OneForm';
import { fetchMarcas, fetchModelos } from 'src/bff';
import { OneFormState } from 'src/types';
import { useFormContext } from 'react-hook-form';

interface BrandModelProps {
	stepName: string;
	brandName?: string;
	modelName?: string;
	marginTop?: string;
	notALayer?: boolean;
	required: boolean;
	formToken?: string;
	nextOnClick?: boolean;
	alternatCTA?: boolean;
	excludeMarcas?: [string];
	excludeModelos?: [string];
}

const SelectsWrapper = styled.div`
	margin-top: 4px;
	display: flex;
	flex-wrap: wrap;
	width: var(--size-grid020);
	@media all and (min-width: 560px) {
		width: var(--size-grid013);
	}
	@media screen and (min-width: 960px) {
		margin-top: 0;
		width: var(--size-grid008);
	}
	@media all and (min-width: 1280px) {
		width: var(--size-grid006);
	}
	@media all and (min-width: 1920px) {
		width: var(--size-grid005);
	}
	@media all and (min-width: 2560px) {
		width: var(--size-grid004);
	}
	label {
		margin-top: 40px;
		width: var(--size-grid009);

		@media all and (max-height: 624px) {
			margin-top: 20px;
		}

		@media all and (min-width: 560px) {
			width: var(--size-grid006);
		}
		@media screen and (min-width: 960px) {
			width: calc(var(--size-grid003) + (var(--size-grid001) / 2));
		}
		@media all and (min-width: 1280px) {
			width: calc(var(--size-grid002) + (var(--size-grid001) / 2));
		}
		@media all and (min-width: 1920px) {
			width: var(--size-grid002);
		}
		@media all and (min-width: 2560px) {
			width: calc(var(--size-grid001) + (var(--size-grid001) / 2));
		}
	}
`;
const MarginDiv = styled.div`
	width: var(--size-grid002);
	@media all and (min-width: 560px) {
		width: var(--size-grid001);
	}
`;

const ButtonWidth = styled.div`
	margin-top: 58px;
	margin-left: 10px;

	button {
		width: 81px;
	}
`;

const ModeloInteresWrapper = styled.div<{ alternatCTA?: boolean }>`
	display: ${props => (props.alternatCTA ? 'flex' : 'block')};
	flex-wrap: ${props => (props.alternatCTA ? 'wrap' : 'nowrap')};

	@media all and (min-width: 560px) {
		gap: ${props => (props.alternatCTA ? '58px' : '0')};
	}
`;

export const SelectMarcaModelo = (props: BrandModelProps) => {
	const {
		stepName,
		brandName = 'brand',
		modelName = 'model',
		notALayer,
		marginTop,
		required,
		formToken,
		nextOnClick,
		alternatCTA,
		excludeMarcas,
		excludeModelos,
	} = props;
	const { handleNextStep } = useOneFormContext();
	const { formData, vehiclesInfo } = useSelector(
		(state: OneFormState) => state,
	);
	const dispatch = useDispatch();
	const { register } = useFormContext();
	const [isAnswered, setIsAnswered] = useState(false);
	const [brand, setBrand] = useState(null);
	const [model, setModel] = useState(null);
	const [brands, setBrands] = useState([]);
	const [models, setModels] = useState([]);

	const getBrandByName = name => {
		return brands ? brands.find(obj => obj.name == name) : null;
	};

	const getBrandByCode = code => {
		return brands ? brands.find(obj => obj.code == code) : null;
	};

	const getModelByName = name => {
		return models ? models.find(obj => obj.name == name) : null;
	};

	const getModelByCode = code => {
		return models ? models.find(obj => obj.code == code) : null;
	};

	const convertToOption = (arr: any) => {
		const name = arr['attributes'].find(obj => obj.name == 'NAME');
		const code = arr['attributes'].find(obj => obj.name == 'CODE');
		return {
			code: code ? code.value : null,
			name: name ? name.value : null,
		};
	};

	const fillBrandsCombo = () => {
		setModels([]);
		if (vehiclesInfo.brands) {
			setBrands(vehiclesInfo.brands);
		} else {
			fetchMarcas(formToken).then(result => {
				if (result?.content) {
					result.content = namesSort(result.content);
					const brands = [];
					result.content.forEach(arr => {
						const brand = convertToOption(arr);
						if (
							!excludeMarcas ||
							!excludeMarcas.includes(brand.name)
						) {
							brands.push(brand);
						}
					});
					setBrands(brands);
					setBrand(null);
					dispatch({
						type: 'UPDATE_VEHICLES',
						payload: { brands: brands },
					});
				}
			});
		}
	};

	const fillModelsCombo = (brandCode: string) => {
		setModels([]);
		if (brandCode == vehiclesInfo.brandSelected) {
			setModels(vehiclesInfo.models);
		} else {
			fetchModelos(brandCode, formToken).then(result => {
				if (result?.content) {
					result.content = namesSort(result.content);
					const models = [];
					result.content.forEach(arr => {
						const model = convertToOption(arr);
						if (
							!excludeModelos ||
							!excludeModelos.includes(model.name)
						) {
							models.push(model);
						}
					});
					setModels(models);
					setModel(null);
					dispatch({
						type: 'UPDATE_VEHICLES',
						payload: { models: models },
					});
				}
			});
		}
	};

	useEffect(() => {
		if (brands.length == 0) {
			fillBrandsCombo();
		}

		if (vehiclesInfo.brandSelected) {
			setBrand(vehiclesInfo.brandSelected);
			fillModelsCombo(vehiclesInfo.brandSelected);
			if (vehiclesInfo.modelSelected) {
				setModel(vehiclesInfo.modelSelected);
			}
		}
	}, []);

	const handleChangeBrand = (e: any) => {
		if (e.target.value) {
			const brand = getBrandByName(e.target.value);
			setBrand(brand);
			fillModelsCombo(brand.code);

			dispatch({
				type: 'UPDATE_VEHICLES',
				payload: { brandSelected: brand, modelSelected: null },
			});
			dispatch({
				type: 'UPDATE_FIELDS',
				payload: { [brandName]: brand.code },
			});
		}
	};

	const handleChangeModel = (e: any) => {
		if (e.target.value) {
			const model = getModelByName(e.target.value);
			setModel(model);

			dispatch({
				type: 'UPDATE_VEHICLES',
				payload: { modelSelected: model },
			});
			dispatch({
				type: 'UPDATE_FIELDS',
				payload: { [modelName]: model.code },
			});
			dispatch({
				type: 'UPDATE_LITERAL_FIELDS_STEPS',
				payload: { [stepName]: brand.name + ' ' + model.name },
			});
		}
	};

	const namesSort = data => {
		data.sort((a, b) => {
			const nameA = a.attributes.find(attr => attr.name === 'NAME').value;
			const nameB = b.attributes.find(attr => attr.name === 'NAME').value;
			if (nameA < nameB) {
				return -1;
			}
			if (nameA > nameB) {
				return 1;
			}
			return 0;
		});
		return data;
	};

	const handleNoModel = () => {
		setBrand(null);
		setModel(null);
		dispatch({
			type: 'UPDATE_VEHICLES',
			payload: { brandSelected: null, modelSelected: null },
		});
		dispatch({ type: 'UPDATE_FIELDS', payload: { [brandName]: null } });
		dispatch({ type: 'UPDATE_FIELDS', payload: { [modelName]: null } });
		dispatch({
			type: 'UPDATE_LITERAL_FIELDS_STEPS',
			payload: { [stepName]: 'No' },
		});
		setIsAnswered(true);
	};

	return (
		<ModeloInteresWrapper alternatCTA={alternatCTA}>
			<SelectsWrapper>
				<SelectExtended
					label="Marca"
					name={brandName}
					options={brands}
					disabled={brands.length == 0}
					isRequired={required ? required : false}
					required
					handleChange={e => {
						//e.preventDefault();
						handleChangeBrand(e);
					}}
					value={brand ? brand.name : ''}
					defaultValue=""
					onClick={() => null}
					setOption={setBrand}
					isFloating={true}
				/>
				<MarginDiv className="just-for-margin" />
				<SelectExtended
					label="Model"
					name={modelName}
					options={models}
					disabled={models.length == 0}
					isRequired={required ? required : false}
					required
					handleChange={e => {
						//e.preventDefault();
						handleChangeModel(e);
					}}
					value={model ? model.name : ''}
					defaultValue=""
					onClick={() => null}
					setOption={setModel}
					isFloating={true}
				/>
			</SelectsWrapper>
			<input
				type="hidden"
				name="Modelo"
				value={isAnswered ? 'true' : model ? model.code : null}
				ref={register({ required: required })}
			/>
			{alternatCTA ? (
				<ButtonWidth>
					<CTA
						emphasis={'secondary'}
						tag="button"
						size={'small'}
						onClick={e => {
							e.preventDefault();
							handleNoModel();
							if (nextOnClick) {
								setTimeout(() => {
									handleNextStep();
								}, 0);
							}
						}}
					>
						No
					</CTA>
				</ButtonWidth>
			) : null}
		</ModeloInteresWrapper>
	);
};
